import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { Button, Switch, Skeleton, Alert } from "antd";
import {
    useCalculateMultipliersFromCurrentStudyMutation,
    useGetCurrentCompassMultipliersTableRowsQuery,
    useGetCurrentCompassMultipliersTableRowOptionsQuery,
    useUpdateCurrentConfigMutation,
    useGetIsUsingCustomMultipliersQuery
} from "../../redux/api/compassMultiplierSettings";

const CompassMultiplierConfigPage = () => {
    const selectedClient = useSelector(
        (state) => state.genericReporting.selectedDropdownValues['client_name']
    );
    const selectedRegion = useSelector(
        (state) => state.genericReporting.selectedDropdownValues['single_select_client_region']
    );
    const [rowData, setRowData] = useState([]);
    const [useCustomMultiplier, setUseCustomMultiplier] = useState(false);

    const { data: currentMultipliersData, isFetching: isLoadingMultipliers } =
        useGetCurrentCompassMultipliersTableRowsQuery({ clientName: selectedClient, clientRegion: selectedRegion }, { skip: !selectedClient || !selectedRegion });
    const { data: multiplierOptionsData, isFetching: isLoadingOptions, error: errorOptions } =
        useGetCurrentCompassMultipliersTableRowOptionsQuery({ clientName: selectedClient, clientRegion: selectedRegion });
    const { data: isUsingCustomMultipliers } = useGetIsUsingCustomMultipliersQuery({ clientName: selectedClient, clientRegion: selectedRegion });
    const [calculateMultipliers] = useCalculateMultipliersFromCurrentStudyMutation();
    const [updateConfig] = useUpdateCurrentConfigMutation();

    useEffect(() => {
        if (isUsingCustomMultipliers) {
            setUseCustomMultiplier(true);
        }
    }, [isUsingCustomMultipliers]);

    useEffect(() => {
        if (multiplierOptionsData) {
            const existingMultipliers = currentMultipliersData || [];
            const existingDrivers = new Set(existingMultipliers.map((m) => m.Driver));
            const completeData = multiplierOptionsData.map((driver) => ({
                Driver: driver,
                Multiplier: existingDrivers.has(driver)
                    ? existingMultipliers.find((m) => m.Driver === driver)?.Multiplier
                    : 0,
            }));
            setRowData(completeData);
            console.log('updated cell value')
        }
    }, [currentMultipliersData, multiplierOptionsData, isLoadingMultipliers, isLoadingOptions]);

    const handleCalculateMultipliers = async () => {
        const { data } = await calculateMultipliers({ clientName: selectedClient, clientRegion: selectedRegion });
        if (data) {
            setRowData(data);
        }
    };

    const handleSaveConfig = async () => {
        await updateConfig({
            clientName: selectedClient,
            clientRegion: selectedRegion,
            useCustomMultiplier,
            newMultipliers: rowData,
        });
    };

    function onCellValueChanged(params) {
        console.log('cell value changed', params);
        const newRows = rowData.map((row) => {
            if (row.Driver === params.data.Driver) {
                return {
                    ...row,
                    Multiplier: params.data.Multiplier,
                };
            }
            return row;
        });
        setRowData(newRows);
    }

    const columns = [
        { headerName: "Driver", field: "Driver", sortable: true, filter: true },
        { headerName: "Multiplier", field: "Multiplier", editable: true },
    ];

    if (isLoadingOptions || isLoadingMultipliers) {
        return <Skeleton active />;
    }

    if (errorOptions) {
        return <Alert message="Failed to load multiplier options" type="error" showIcon />;
    }

    return (
        <div style={{ margin: "1em", marginBottom: "5em" }}>
            <h1>Compass Multiplier Config Page</h1>
            <Switch
                checked={useCustomMultiplier}
                onChange={setUseCustomMultiplier}
                checkedChildren="Custom Multiplier"
                unCheckedChildren="Default"
            />
            <Button onClick={handleCalculateMultipliers} style={{ margin: "10px" }}>
                Use Current Study
            </Button>
            <Button onClick={handleSaveConfig} type="primary">
                Save Config
            </Button>
            <div className="ag-theme-alpine" style={{ height: '40vw', width: "100%" }}>
                <AgGridReact
                    rowData={rowData}
                    columnDefs={columns}
                    onCellValueChanged={onCellValueChanged}
                    defaultColDef={{ resizable: true }}
                />
            </div>
        </div>
    );
};

export default CompassMultiplierConfigPage;
